<template>
  <div
    class="c-grid-view-item"
    :class="{ 'c-grid-view-item--folder': item.type === 'folder', 'is-selected': isSelected, 'is-upload-target': dragMode === 'upload' }"
    :title="getTitle(item)"
    @dragstart="$emit('dragstart', $event)"
    @drop="handleDrop"
    @dragover="handleDragEnter"
    @dragleave="handleDragLeave"
  >
    <div
      v-if="isTooltipVisible"
      class="tooltip"
    >
      {{ tooltip }}
    </div>
    <div
      class="c-grid-view-item__preview"
      :class="{ active: isSelected, 'is-actions-open': isActionsMenuOpen }"
      role="button"
      tabindex="0"
      :aria-pressed="isSelected ? 'true' : 'false'"
      :aria-label="selectionAriaLabel"
      @click="$emit('click', item)"
      @dblclick="handleDoubleClick"
      @keydown.enter.prevent="$emit('click', item)"
      @keydown.space.prevent="$emit('click', item)"
    >
      <div
        class="c-grid-view-item__preview--item"
        :class="{ active: isSelected || Boolean(dragMode) }"
      >
        <PSDPreviewBadge
          v-if="parsedTitle"
          left="5"
          top="5"
          variant="success"
          :title="parsedTitle"
        />
        <b-badge
          v-if="isLimited"
          class="limited-label"
          pill
          variant="warning"
        >
          Limited
        </b-badge>
        <span
          v-for="action in cornerActions"
          :key="action.name"
          class="c-grid-view-item__action c-grid-view-item__action--corner"
          :class="{ 'is-active': isActionActive(action) }"
          :title="actionTitle(action)"
          @click.stop="$emit('action', { name: action.name, item })"
        >
          <b-icon :icon="actionIcon(action)" />
        </span>
        <b-icon
          v-if="!cornerActions.length && item.is_favorite"
          icon="bookmark-fill"
          class="favorite-icon"
        />
        <b-badge
          v-if="hasProcessingErrors"
          class="error-badge"
          pill
          variant="danger"
          title="Upload failed or stuck. Please delete and try again."
        >
          <i class="fas fa-exclamation-triangle" /> Error
        </b-badge>
        <ItemView
          :item="item"
          @image-missing="isImageMissing = true"
        />
        <div
          v-if="showCounter"
          class="select-same-image-container"
          @click.stop
        >
          <span
            class="mines"
            @click.stop="$emit('count-change', -1)"
          >-</span>
          <div class="counter">
            {{ count }}
          </div>
          <span
            class="plus"
            @click.stop="$emit('count-change', 1)"
          >+</span>
        </div>
        <div
          v-if="item.is_processing && !hasProcessingErrors"
          class="c-grid-view-item__preview--progress w-100"
        >
          <b-progress
            class="a-progressing__bar"
            :value="item.percent"
            variant="success"
            striped
            animated
          />
        </div>
        <input
          v-if="showCheckbox && item.type !== 'folder'"
          type="checkbox"
          class="form-check-input c-grid-view-item__checkbox"
          :checked="isSelected"
          @click.stop="$emit('click', item)"
        >
        <button
          v-if="showDownloadIcon && item.type !== 'folder'"
          type="button"
          class="c-grid-view-item__download"
          title="Download"
          @click.stop="$emit('download', item)"
        >
          <b-icon
            icon="download"
            font-scale="1.1"
          />
        </button>
        <div class="c-grid-view-item__preview--overlay">
          <span
            v-if="showCopyToFileBrowser"
            class="copy-to-browser"
            title="Copy to File Browser..."
            @click.stop="$emit('copy-to-file-browser', item)"
          >
            <CopyIcon
              :width="16"
              :height="16"
            />
          </span>
          <span
            v-if="showMagnifier && !isImageMissing"
            class="magnifier"
            @click.stop="$emit('open-preview', item)"
          >
            <SearchIcon
              :width="16"
              :height="16"
            />
          </span>
          <span
            v-if="showDragHandle"
            class="drag-handle"
            @click.stop
            @mousedown.stop
          >
            <GripVerticalIcon
              :width="16"
              :height="16"
            />
          </span>
          <span
            v-for="action in overlayActions"
            :key="action.name"
            class="c-grid-view-item__action"
            :class="`c-grid-view-item__action--${action.variant || 'default'}`"
            :title="actionTitle(action)"
            @click.stop="$emit('action', { name: action.name, item })"
          >
            <b-icon :icon="actionIcon(action)" />
          </span>
          <ItemActionsDropdown
            v-if="isAvailableOverlay && item.name !== '...'"
            :item="item"
            @open="isActionsMenuOpen = true"
            @close="isActionsMenuOpen = false"
          />
        </div>
        <div
          v-if="item.type === 'folder'"
          class="c-grid-view-item__folder_name"
        >
          <span
            v-b-tooltip.hover.viewport
            :title="folderNameTitle"
          >{{ item.name }}</span>
          <button
            v-if="showEditButton"
            class="folder-edit-btn"
            title="Rename folder"
            @click.stop="$emit('edit', item)"
          >
            <i class="fa fa-edit" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="item.type !== 'folder'"
      v-b-tooltip.hover.viewport
      class="c-grid-view-item__name"
      :class="item.asset_type === 'salsify' ? 'c-grid-view-item__name--external' : 'c-grid-view-item__name--regular'"
      :title="fileDisplayName"
    >
      {{ fileDisplayName }}
    </div>
    <div
      v-if="item?.asset_type === 'salsify' && item?.is_product"
      class="c-grid-view-item__external-id"
    >
      <span>{{ item?.salsify_id }}</span>
    </div>
  </div>
</template>

<script>
import ItemView from "./ItemView.vue";
import ItemActionsDropdown from "./item-overlays/ItemActionsDropdown.vue";
import { FILE_TYPE, TYPE } from "@frontend/constants/file-image-types";
import PSDPreviewBadge from "@frontend/components/common/atoms/PSDPreviewBadge.vue";
import { getFileDisplayName, isProcessingError } from '@frontend/components/modules/file-browser/helpers.js';
import {
  Copy as CopyIcon,
  GripVertical as GripVerticalIcon,
  Search as SearchIcon
} from 'lucide-vue';

export default {
  name: "GridItem",
  components: {
    PSDPreviewBadge,
    ItemView,
    ItemActionsDropdown,
    CopyIcon,
    GripVerticalIcon,
    SearchIcon
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isAvailableOverlay: {
      type: Boolean,
      default: false,
    },
    isDraggingFolders: {
      type: Boolean,
      default: true,
    },
    showEditButton: {
      type: Boolean,
      default: false,
    },
    showDragHandle: {
      type: Boolean,
      default: false,
    },
    showMagnifier: {
      type: Boolean,
      default: false
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    uploadOnDrop: {
      type: Boolean,
      default: false
    },
    showDownloadIcon: {
      type: Boolean,
      default: false
    },
    // Multi-placement count. Legacy class names are kept so existing styling still applies.
    showCounter: {
      type: Boolean,
      default: false
    },
    count: {
      type: Number,
      default: 1
    },
    showCopyToFileBrowser: {
      type: Boolean,
      default: false
    },
    // `{ name, icon, activeIcon?, title?, placement?, variant?, isVisible?, isActive? }`; the last three may be `(item) => …`.
    actions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dragMode: null,
      tooltip: '',
      isTooltipVisible: false,
      isImageMissing: false,
      // Keep overlay visible while the actions menu is open so Popper keeps a valid anchor
      // when the cursor leaves the card (hover alone would hide the toggle).
      isActionsMenuOpen: false,
    };
  },
  computed: {
    folderNameTitle() {
      const name = this.item?.name;
      if (!name || name === '...') {
        return '';
      }

      return name;
    },
    selectionAriaLabel() {
      const name = this.item?.name;
      if (!name || name === '...') {
        return this.item?.tooltip || 'Go back';
      }

      return this.isSelected ? `Deselect ${name}` : `Select ${name}`;
    },
    fileDisplayName() {
      return getFileDisplayName(this.item);
    },
    isPsdParsed() {
      return this.item.type === FILE_TYPE.PSD && this.item.is_psd_parsed;
    },
    isFigParsed() {
      return this.item.type === FILE_TYPE.FIGMA && this.item.is_fig_parsed;
    },
    isIdmlParsed() {
      return this.item.type === FILE_TYPE.IDML && this.item.is_idml_parsed;
    },
    parsedTitle() {
      if (this.isPsdParsed) return 'PSD Parsed';
      if (this.isFigParsed) return 'FIG Parsed';
      if (this.isIdmlParsed) return 'IDML Parsed';
      return null;
    },
    isLimited() {
      return this.item?.is_limited ?? false;
    },
    hasProcessingErrors() {
      return Boolean(this.item.upload_error) || isProcessingError(this.item);
    },
    cornerActions() {
      return this.visibleActions('corner');
    },
    overlayActions() {
      return this.visibleActions('overlay');
    }
  },
  watch: {
    // The grid reuses tiles when ids repeat, so a tile that fell back once must not stay broken.
    item() {
      this.isImageMissing = false;
      this.isActionsMenuOpen = false;
    }
  },
  methods:{
    visibleActions(placement) {
      return this.actions.filter((action) => (action.placement || 'overlay') === placement
        && this.resolve(action.isVisible, true));
    },
    resolve(value, fallback) {
      if (value === undefined) {
        return fallback;
      }

      return typeof value === 'function' ? value(this.item) : value;
    },
    isActionActive(action) {
      return Boolean(this.resolve(action.isActive, false));
    },
    actionIcon(action) {
      return this.isActionActive(action) && action.activeIcon ? action.activeIcon : action.icon;
    },
    actionTitle(action) {
      return this.resolve(action.title, '');
    },
    handleDoubleClick() {
      if (this.item?.type === 'link' && this.item?.url) {
             window.open(this.item.url, '_blank');
      } else {
             this.$emit('dblclick', this.item);
      }
    },
    handleDragEnter(event) {
        if (this.item.type !== TYPE.FOLDER) {
            return;
        }

        if (this.uploadOnDrop && event.dataTransfer?.types?.includes('Files')) {
            this.dragMode = 'upload';

            return;
        }

        if (this.item.is_root && !this.isDraggingFolders) {
            return;
        }
        this.dragMode = 'move';
        this.tooltip = 'Move to ' + this.item.destination_name;
        this.isTooltipVisible = this.item.destination_name ? true : false;
    },
    handleDrop($event) {
      this.$emit('drop', $event);
      this.clearDragState();
    },
    handleDragLeave(event) {
        if (this.item.type === TYPE.FOLDER){
          this.clearDragState();
        }
    },
    clearDragState() {
      this.dragMode = null;
      this.tooltip = '';
      this.isTooltipVisible = false;
    },
    getTitle(item) {
      if (item?.tooltip) {
        return item.tooltip
      }

      if (item?.name === '...') {
        return 'Go back';
      }

      return '';
    }
  }
}
</script>

<style scoped lang="scss">
  .c-grid-view-item {
    width: 100%;
    height: fit-content;
    cursor: pointer;

    &--folder .c-grid-view-item__preview {
      overflow: hidden;
      z-index: 1;
      border: 1px solid #c5cdd6;
      border-radius: 8px;
      background: #fff;

      &:hover,
      &:focus-within,
      &.is-actions-open {
        z-index: 5;
        border-color: rgba(22, 163, 139, 0.4);
      }
    }

    &--folder.is-selected .c-grid-view-item__preview,
    &--folder.is-selected .c-grid-view-item__preview:hover,
    &--folder.is-selected .c-grid-view-item__preview:focus-within,
    &--folder.is-selected .c-grid-view-item__preview.is-actions-open {
      border-width: 2px;
      border-color: #16a38b;
      background: rgba(22, 163, 139, 0.05);
      box-shadow: none;
      // Extra 1px border would grow the box; pull it back so the tile stays put.
      margin: -1px;
    }

    // Icon well is a fixed height; the name wraps below it so every icon shares a Y.
    &--folder .c-grid-view-item__preview--item {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      min-height: 0;
    }

    &--folder ::v-deep .c-file-browser__item {
      flex: 0 0 var(--c-item-folder-preview-height, 88px);
      height: var(--c-item-folder-preview-height, 88px) !important;
      max-height: var(--c-item-folder-preview-height, 88px);
    }

    &.is-upload-target .c-grid-view-item__preview {
      border-color: transparent;
      outline: 2px dashed #16a38b;
      outline-offset: -2px;
      background: rgba(22, 163, 139, 0.05);
    }

    &__preview {
      float: inherit;
      border: 1px solid #e5e5e5;
      background: white;
      box-sizing: border-box;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      border-radius: 8px;
      position: relative;
      overflow: hidden;
      // Avoid hover `transform`: it creates a containing block for position:fixed
      // Popper menus, so the menu jumps when hover ends.
      transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover,
      &.is-actions-open {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      }

      // Keep the overlay mounted while the menu is open (mouse may leave the card).
      &.is-actions-open {
        .c-grid-view-item__preview--overlay {
          display: flex;
        }
      }

      &--item {
        min-height: 0;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: rgba(249, 250, 251, 0.3);
        padding: 0;
        margin: 0;

        transition: all 0.2s;

        &:hover {
          border-color: rgba(22, 163, 139, 0.3);
          background: rgba(249, 250, 251, 0.5);
        }

        &.active {
          border-color: #16a38b;
          background: rgba(22, 163, 139, 0.05);
        }

        &:hover .c-grid-view-item__checkbox {
          display: block;
        }

        &:hover .c-grid-view-item__download {
          display: flex;
        }
      }

      &.active {
        border-color: #16a38b;
        box-shadow: 0 0 0 3px rgba(22, 163, 139, 0.1);
      }

      .limited-label {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 2;
      }

      .error-badge {
        position: absolute;
        top: 8px;
        left: 8px;
        z-index: 2;
        font-size: 0.7rem;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
      }

      &--overlay {
        position: absolute;
        top: 4px;
        right: 4px;
        display: none;
        z-index: 3;
        align-items: center;
        gap: 2px;
        background: rgba(255, 255, 255, 0.85);
        border-radius: 6px;
        padding: 2px 4px;

        // Keep actions visible while the dropdown menu is open
        &:focus-within {
          display: flex;
        }
      }

      &:hover &--overlay,
      &:focus-within &--overlay {
        display: flex;
      }

      .c-grid-view-item__checkbox {
        appearance: none;
        -webkit-appearance: none;
        position: absolute;
        top: 8px;
        left: 8px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid #f0f2f4;
        background: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        display: none;
        z-index: 4;
        margin: 0;

        &:checked {
          display: block;
          background: #16a38b url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e") center/14px no-repeat;
          border-color: #16a38b;
        }
      }

      .c-grid-view-item__download {
        appearance: none;
        -webkit-appearance: none;
        position: absolute;
        bottom: 8px;
        right: 8px;
        width: 26px;
        height: 26px;
        border-radius: 6px;
        border: none;
        background: rgba(255, 255, 255, 0.9);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
        z-index: 4;
        color: #495057;

        &:hover {
          background: #fff;
          color: #16a38b;
        }
      }

      &--progress {
        position: absolute;
        left: 0;
        top: calc(50% - 8px);
        z-index: 2;
      }
    }

    &:not(.c-grid-view-item--folder) {
      .c-grid-view-item__preview {
        aspect-ratio: 1 / 1;
        width: 100%;
      }

      .c-grid-view-item__preview--item {
        height: 100%;
      }

      ::v-deep .c-file-browser__item img {
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: contain;
      }
    }

    &__name {
      margin-top: 8px;
      padding: 0 8px;
      font-family: "Nunito", sans-serif;
      font-weight: 600;
      color: #1f2937;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
      line-height: 1.4;

      &--regular {
        font-size: 12px;
        text-align: center;
      }

      &--external {
        font-size: 11px;
      }
    }

    &__external-id {
      padding: 0 8px;
      font-size: 11px;
    }

    .tooltip {
      position: absolute;
      background-color: #1f2937;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      font-family: "Nunito", sans-serif;
      opacity: 1;
      transition: opacity 0.3s ease;
      z-index: 10;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  }
  
  .folder-items .c-grid-view-item {
    &__preview {
      background: #fff;
      border: 1px solid #c5cdd6;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

      &--item {
        min-height: 0;
        justify-content: flex-start;
        align-items: stretch;
        background: #f3f4f6;
        border-radius: 0;
      }
    }

    &.is-selected {
      .c-grid-view-item__preview,
      .c-grid-view-item__preview:hover,
      .c-grid-view-item__preview:focus-within,
      .c-grid-view-item__preview.is-actions-open {
        border-width: 2px;
        border-color: #16a38b;
        background: rgba(22, 163, 139, 0.05);
        box-shadow: none;
        margin: -1px;
      }

      .c-grid-view-item__folder_name {
        background: rgba(22, 163, 139, 0.05);
      }
    }
  }

  .folder-items .c-grid-view-item__folder_name {
    width: 100%;
    box-sizing: border-box;
    flex: 0 0 auto;
    overflow: hidden;
    padding: 8px 10px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    font-family: "Nunito", sans-serif;
    line-height: 1.4;
    cursor: pointer;
    color: rgba(34, 38, 42);
    display: flex;
    justify-content: space-between;
    align-items: center;

    > span {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .folder-edit-btn {
      background: none;
      border: none;
      padding: 2px 4px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.2s;
      color: #747474;
      flex-shrink: 0;

      &:hover {
        color: #16a38b;
      }
    }
  }

  .folder-items .c-grid-view-item:hover .folder-edit-btn {
    opacity: 1;
  }

  // Rescued from the modal stylesheet, whose selectors stopped matching once the panes became custom elements.
  .select-same-image-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    position: absolute;
    top: 5px;
    right: 5px;
    padding: 2px;
    z-index: 10;

    .mines,
    .plus {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #ffffff;
      color: #479382;
      font-size: 16px;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);

      &:hover {
        color: #fec350;
      }
    }

    .counter {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
      border: 1px solid #ffffff;
      border-radius: 6px;
      background: #479382;
      color: #fff;
      font-size: 14px;
      font-weight: 700;
    }
  }

  .favorite-icon {
    position: absolute;
    font-size: 0.7rem;
    top: 8px;
    left: 8px;
    color: #16a38b;
    z-index: 2;
  }

  // The overlay these sit in is a near-white pill, so a white glyph is invisible against it.
  .c-grid-view-item__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: #9ca3af;
    cursor: pointer;

    &:hover {
      color: #374151;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .c-grid-view-item__action--danger:hover {
    color: #ef4444;
    background-color: rgba(239, 68, 68, 0.1);
  }

  .c-grid-view-item__action--corner {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 2;
    width: 24px;
    height: 24px;
    color: #94a3b8;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s;

    &.is-active {
      color: #16a38b;
      opacity: 1;
    }

    &:hover {
      background-color: rgba(15, 23, 42, 0.06);
    }
  }

  .c-grid-view-item:hover .c-grid-view-item__action--corner {
    opacity: 1;
  }

  .copy-to-browser,
  .drag-handle,
  .magnifier {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 4px;
    color: #9ca3af;
    line-height: 1;

    &:hover {
      color: #374151;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .drag-handle {
    cursor: grab;
  }

  .copy-to-browser,
  .magnifier {
    cursor: pointer;
  }
</style>
